import React, { useEffect, useState } from "react";
import { Loader2, Sparkles } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

const empty = (date) => ({ date, description: "", amount: "", kind: "expense", group: "", method: "Nakit", note: "", account_id: "", from_account_id: "", to_account_id: "", customer_id: "", staff_id: "" });
const isTransferKind = (k) => k === "transfer";

export default function CashEntryForm({ open, onClose, item, groups, defaultDate, onSaved, accounts = [] }) {
  const [f, setF] = useState(empty(defaultDate));
  const [auto, setAuto] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sugs, setSugs] = useState([]);
  const [showSugs, setShowSugs] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [staff, setStaff] = useState([]);
  const active = accounts.filter((a) => a.active !== false);
  const accName = (id) => accounts.find((a) => a.id === id)?.name || "";

  useEffect(() => {
    if (!open) return;
    const first = (type) => active.find((a) => a.type === type)?.id || active[0]?.id || "";
    if (item) setF({ ...item, amount: String(item.amount), account_id: item.account_id || "", from_account_id: item.from_account_id || "", to_account_id: item.to_account_id || "", customer_id: "", staff_id: "" });
    else setF({ ...empty(defaultDate), account_id: first("cash") });
    setAuto(!item); setSugs([]); setShowSugs(false);
    if (!item) {
      api.get("/customers").then((r) => setCustomers(r.data)).catch(() => {});
      api.get("/staff").then((r) => setStaff(r.data)).catch(() => {});
    }
    /* eslint-disable-next-line */ }, [open]);

  useEffect(() => {
    if (!open || !showSugs) return;
    const t = setTimeout(() => api.get("/cashbook/descriptions", { params: { q: f.description, kind: f.kind } }).then((r) => setSugs(r.data)).catch(() => setSugs([])), 200);
    return () => clearTimeout(t);
  }, [f.description, f.kind, open, showSugs]);

  const pickSug = (s) => {
    setF((x) => ({ ...x, description: s.description, group: s.group || x.group }));
    setAuto(false); setShowSugs(false);
  };

  useEffect(() => {
    if (!open || !auto || isTransferKind(f.kind) || f.description.trim().length < 3) return;
    const t = setTimeout(() => api.get("/cashbook/suggest", { params: { text: f.description, kind: f.kind } }).then((r) => setF((x) => ({ ...x, group: r.data.group }))), 300);
    return () => clearTimeout(t);
  }, [f.description, f.kind, auto, open]);

  const isTransfer = f.kind === "transfer";
  const opts = f.kind === "income" ? groups.income : groups.expense;
  const save = async () => {
    const amount = parseFloat(f.amount);
    if (!f.description.trim() || !(amount > 0)) { toast.error("Açıklama ve tutar gerekli"); return; }
    if (isTransfer && (!f.from_account_id || !f.to_account_id)) { toast.error("Kaynak ve hedef hesap seçin"); return; }
    if (isTransfer && f.from_account_id === f.to_account_id) { toast.error("Kaynak ve hedef hesap aynı olamaz"); return; }
    if (!isTransfer && !f.account_id) { toast.error("Hesap seçin"); return; }
    setSaving(true);
    try {
      const body = { date: f.date, description: f.description, amount, kind: f.kind, group: isTransfer ? "VIRMAN" : f.group, method: f.method || "Nakit", note: f.note || "",
        account_id: isTransfer ? null : f.account_id, from_account_id: isTransfer ? f.from_account_id : null, to_account_id: isTransfer ? f.to_account_id : null,
        customer_id: (!item && f.kind === "income" && f.customer_id) ? f.customer_id : null,
        staff_id: (!item && f.kind === "expense" && f.staff_id) ? f.staff_id : null };
      if (item?.id) await api.put(`/cashbook/entries/${item.id}`, body); else await api.post("/cashbook/entries", body);
      toast.success(item ? "Kayıt güncellendi" : "Kayıt eklendi"); onClose(); onSaved();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="cash-entry-dialog">
        <DialogHeader><DialogTitle className="font-heading">{item ? "Kaydı Düzenle" : "Kasa Kaydı Ekle"}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-3 gap-1 p-1 rounded-xl bg-muted/50" data-testid="cash-kind-tabs">
          {[["expense", "Gider"], ["income", "Gelir"], ["transfer", "Virman"]].map(([k, l]) => (
            <button key={k} onClick={() => setF({ ...f, kind: k, group: "", from_account_id: k === "transfer" ? (f.from_account_id || active.find((a) => a.type === "cash")?.id || "") : f.from_account_id, to_account_id: k === "transfer" ? (f.to_account_id || active.find((a) => a.type === "bank")?.id || "") : f.to_account_id, description: k === "transfer" && !f.description ? "Virman" : f.description })} data-testid={`cash-kind-${k}`}
              className={`h-9 rounded-lg text-sm font-semibold ${f.kind === k ? (k === "income" ? "bg-emerald-500/15 text-emerald-400" : k === "transfer" ? "bg-sky-500/15 text-sky-400" : "bg-rose-500/15 text-rose-400") + " shadow" : "text-muted-foreground"}`}>{l}</button>
          ))}
        </div>
        {isTransfer && <p className="text-xs text-muted-foreground -mt-1" data-testid="cash-transfer-hint">Virman gelir/gider sayılmaz; seçilen iki hesap arasında para taşır (örn. kredi kartı borcu ödemesi: Banka → Kart).</p>}
        <div className="grid grid-cols-2 gap-4">
          <F label="Tarih"><Input type="date" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} data-testid="cash-date" className="font-mono" /></F>
          <F label="Tutar (₺)"><Input type="number" step="0.01" min="0" value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} data-testid="cash-amount" className="font-mono" placeholder="0,00" /></F>
          <div className="col-span-2 relative"><F label="Açıklama">
            <Input value={f.description} onChange={(e) => { setF({ ...f, description: e.target.value }); setShowSugs(true); }}
              onFocus={() => setShowSugs(true)} onBlur={() => setTimeout(() => setShowSugs(false), 150)} autoComplete="off"
              data-testid="cash-description" placeholder="Örn: Tavuk alımı – Fırat Et, Ekim kirası, mazot…" />
          </F>
            {showSugs && sugs.length > 0 && (
              <ul className="absolute z-50 left-0 right-0 mt-1 rounded-lg border border-border bg-popover shadow-xl max-h-56 overflow-y-auto" data-testid="cash-description-suggestions">
                {sugs.map((s, i) => (
                  <li key={i}>
                    <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => pickSug(s)} data-testid={`cash-suggestion-${i}`}
                      className="w-full text-left px-3 py-2 text-sm hover:bg-accent flex items-center justify-between gap-2">
                      <span className="truncate">{s.description}</span>
                      <span className="text-[10px] text-muted-foreground shrink-0">{opts[s.group] || ""}{s.count > 1 ? ` · ${s.count}×` : ""}</span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
          {!isTransfer && <F label={<span className="flex items-center gap-1">Grup {auto && <Sparkles className="h-3 w-3 text-primary" title="Otomatik" />}</span>}>
            <Select value={f.group} onValueChange={(v) => { setF({ ...f, group: v }); setAuto(false); }}>
              <SelectTrigger data-testid="cash-group"><SelectValue placeholder="Otomatik seçilecek" /></SelectTrigger>
              <SelectContent>{Object.entries(opts).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
            </Select>
          </F>}
          {!item && !isTransfer && f.kind === "income" && (
            <div className="col-span-2"><F label="Müşteri (opsiyonel — tahsilat carisinden düşülür)">
              <Select value={f.customer_id || "none"} onValueChange={(v) => setF({ ...f, customer_id: v === "none" ? "" : v })}>
                <SelectTrigger data-testid="cash-customer"><SelectValue placeholder="Müşteri seçin" /></SelectTrigger>
                <SelectContent><SelectItem value="none">— Yok —</SelectItem>{customers.map((c) => <SelectItem key={c.id} value={c.id}>{c.short_name || c.name}</SelectItem>)}</SelectContent>
              </Select>
            </F></div>
          )}
          {!item && !isTransfer && f.kind === "expense" && (
            <div className="col-span-2"><F label="Personel (opsiyonel — alacağından düşülür)">
              <Select value={f.staff_id || "none"} onValueChange={(v) => setF({ ...f, staff_id: v === "none" ? "" : v })}>
                <SelectTrigger data-testid="cash-staff"><SelectValue placeholder="Personel seçin" /></SelectTrigger>
                <SelectContent><SelectItem value="none">— Yok —</SelectItem>{staff.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
              </Select>
            </F></div>
          )}
          {isTransfer ? (
            <>
              <F label="Kaynak Hesap"><AccountSelect value={f.from_account_id} onChange={(v) => setF({ ...f, from_account_id: v })} accounts={active} testId="cash-from-account" /></F>
              <F label="Hedef Hesap"><AccountSelect value={f.to_account_id} onChange={(v) => setF({ ...f, to_account_id: v })} accounts={active} testId="cash-to-account" /></F>
              {f.from_account_id && f.to_account_id && <p className="col-span-2 text-xs text-sky-400 -mt-2" data-testid="cash-transfer-preview">{accName(f.from_account_id)} → {accName(f.to_account_id)}</p>}
            </>
          ) : (
            <F label="Hesap"><AccountSelect value={f.account_id} onChange={(v) => setF({ ...f, account_id: v })} accounts={active} testId="cash-method" /></F>
          )}
          <div className="col-span-2"><F label="Not (opsiyonel)"><Input value={f.note || ""} onChange={(e) => setF({ ...f, note: e.target.value })} data-testid="cash-note" /></F></div>
        </div>
        {f.group && !isTransfer && <p className="text-xs text-muted-foreground" data-testid="cash-group-hint">Grup: <b>{opts[f.group]}</b>{auto ? " (açıklamadan otomatik seçildi — değiştirebilirsiniz)" : ""}</p>}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>İptal</Button>
          <Button onClick={save} disabled={saving} data-testid="cash-save" className="gap-2">{saving && <Loader2 className="h-4 w-4 animate-spin" />} Kaydet</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const TYPE_LABEL = { cash: "Nakit Kasa", bank: "Banka", card: "Kredi Kartı" };
const AccountSelect = ({ value, onChange, accounts, testId }) => (
  <Select value={value || ""} onValueChange={onChange}>
    <SelectTrigger data-testid={testId}><SelectValue placeholder="Hesap seçin" /></SelectTrigger>
    <SelectContent>
      {["cash", "bank", "card"].map((t) => accounts.filter((a) => a.type === t).map((a) => (
        <SelectItem key={a.id} value={a.id}><span className="text-muted-foreground text-xs mr-1.5">{TYPE_LABEL[t]} ·</span>{a.name}</SelectItem>
      )))}
    </SelectContent>
  </Select>
);

const F = ({ label, children }) => (
  <div className="space-y-2"><Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{label}</Label>{children}</div>
);
